import React, { useState, useEffect } from 'react';
import styles from './ColorPicker.module.css';

interface ColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  defaultValue?: string;
  disabled?: boolean;
}

export const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  value,
  onChange,
  defaultValue = '#000000',
  disabled = false,
}) => {
  const [hexInput, setHexInput] = useState(value);
  const [isValidHex, setIsValidHex] = useState(true);

  // Sync hex input when value changes externally
  useEffect(() => {
    setHexInput(value);
  }, [value]);

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setHexInput(newColor);
    onChange(newColor);
    setIsValidHex(true);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setHexInput(input);

    // Validate hex color
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    const isValid = hexRegex.test(input);
    setIsValidHex(isValid);

    if (isValid) {
      onChange(input);
    }
  };

  const handleReset = () => {
    setHexInput(defaultValue);
    onChange(defaultValue);
    setIsValidHex(true);
  };

  const isCustomValue = value !== defaultValue;

  return (
    <div className={styles.colorPicker}>
      <label className={styles.label}>{label}</label>
      <div className={styles.pickerContainer}>
        <input
          type="color"
          value={value}
          onChange={handleColorChange}
          disabled={disabled}
          className={styles.nativePicker}
          title="Click to open color picker"
        />
        <input
          type="text"
          value={hexInput}
          onChange={handleHexInputChange}
          disabled={disabled}
          className={`${styles.hexInput} ${!isValidHex ? styles.invalid : ''}`}
          placeholder="#000000"
        />
        {isCustomValue && (
          <button
            type="button"
            onClick={handleReset}
            disabled={disabled}
            className={styles.resetButton}
            title={`Reset to ${defaultValue}`}
          >
            Reset
          </button>
        )}
      </div>
      {!isValidHex && (
        <span className={styles.errorMessage}>Invalid hex color (e.g., #FF0000)</span>
      )}
    </div>
  );
};
